import { useCallback, useEffect, useRef } from 'react';
import { useUIStore, CHAT_WIDTH_LIMITS } from '@/state/uiStore';
import { cn } from '@/lib/cn';
import { ChatPanel } from '@/chat/ChatPanel';

export function ResizableChatPanel() {
  const open = useUIStore((s) => s.chatOpen);
  const width = useUIStore((s) => s.chatWidth);
  const setWidth = useUIStore((s) => s.setChatWidth);

  const draggingRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const next = window.innerWidth - e.clientX;
      setWidth(next);
    },
    [setWidth],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const max = Math.min(CHAT_WIDTH_LIMITS.max, window.innerWidth - 320);
      if (width > max) setWidth(max);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [width, setWidth]);

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'relative flex shrink-0 flex-col border-l border-border bg-background transition-[width] duration-200 ease-out',
      )}
      style={{ width: open ? width : 0, overflow: 'hidden' }}
    >
      {open && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize bg-transparent hover:bg-ring/50"
        />
      )}
      <ChatPanel />
    </aside>
  );
}
